import moment from 'moment';
import { parsePeriodicity } from '../../utils';
import { resolveBillCurrency } from '../Balance/balanceTotals';
import { getLabel } from '../../utils/labelStore';

/**
 * Ports DynamicListTableNew.js's currency/date/boolean export-formatting
 * logic, reusing the existing utils.js columnInfo arrays as-is. `dataName`
 * must be passed exactly as it was to <DynamicListTableNew dataName=.../>
 * at each call site — the currency switch below keys off it verbatim
 * (including its pre-existing 'debtsDues' vs 'creditDues' inconsistency)
 * so formatting doesn't silently change.
 */

function showCurrency(currency, value) {
	if (currency === 'UYU') return '$U ' + value;
	if (currency === 'USD') return 'U$S ' + value;
	return value;
}

/**
 * `keepZero` is used by the `amount` columns only: a 0 interest or balance has
 * to export as 0, not 'N/A'. Every other column keeps the legacy behaviour of
 * treating 0 as missing.
 */
function getNestedValue(item, key, { keepZero = false } = {}) {
	const keys = key.split('.');
	let value = item;
	for (const part of keys) {
		const next = value ? value[part] : undefined;
		if (next === undefined || next === null || next === '' || (next === 0 && !keepZero)) {
			return undefined;
		}
		value = next;
	}
	return value;
}

function formatCurrency(item, key, nestedValue, dataName) {
	switch (dataName) {
	case 'relWarranties':
		return showCurrency(item.warranty?.debt?.currency || item.warranty?.asset?.currency, nestedValue);
	case 'bills':
		return showCurrency(resolveBillCurrency(item), nestedValue);
	case 'debtDues':
		return showCurrency(item.debt?.currency, nestedValue);
	case 'creditDues':
		return showCurrency(item.credit?.currency, nestedValue);
	default:
		return showCurrency(item.currency, nestedValue);
	}
}

function isVisibleColumn(colDef) {
	return !colDef.toExport;
}

function formatExportValue(item, key, colDef, dataName) {
	// Virtual columns (e.g. the per-currency amount split) resolve their own cell.
	if (typeof colDef.value === 'function') return colDef.value(item);
	// `amount` is the plain-number counterpart of `currency`: no symbol, so the
	// currency lives in its own column and Excel can sum the figures.
	if (colDef.type === 'amount') {
		const amountValue = getNestedValue(item, key, { keepZero: true });
		return amountValue === undefined ? 'N/A' : amountValue;
	}
	const nestedValue = getNestedValue(item, key);
	if (nestedValue === undefined) return 'N/A';
	if (key === 'periodicity') return parsePeriodicity(nestedValue.toString());
	if (colDef.type === 'boolean') return nestedValue ? 'Si' : 'No';
	if (colDef.type === 'date') {
		const date = moment.utc(nestedValue);
		return date.isValid() ? date.format('DD-MM-YYYY') : 'N/A';
	}
	if (colDef.type === 'currency') return formatCurrency(item, key, nestedValue, dataName);
	return nestedValue;
}

const isCurrencyField = (key) => key === 'currency' || key.endsWith('.currency');

/**
 * With `currencyOf`, the currency gets its own column (placed right before
 * the first amount column, replacing any existing currency column) and
 * amounts are exported as plain numbers instead of '$U 100' / 'U$S 100'.
 */
export function buildReportExportRows(columnInfo, dataName, rows, { currencyOf } = {}) {
	let visibleColumns = columnInfo.filter((col) => isVisibleColumn(col[Object.keys(col)[0]]));
	let currencyInsertAt = -1;
	if (currencyOf) {
		visibleColumns = visibleColumns.filter((col) => !isCurrencyField(Object.keys(col)[0]));
		currencyInsertAt = visibleColumns.findIndex((col) => col[Object.keys(col)[0]].type === 'currency');
		if (currencyInsertAt === -1) currencyInsertAt = visibleColumns.length;
	}
	const currencyLabel = getLabel('field.currency');

	return rows.map((item) => {
		const exportRow = {};
		visibleColumns.forEach((col, index) => {
			if (index === currencyInsertAt) exportRow[currencyLabel] = currencyOf(item) || 'N/A';
			const key = Object.keys(col)[0];
			const colDef = col[key];
			exportRow[colDef.label] = currencyOf && colDef.type === 'currency'
				? (getNestedValue(item, key, { keepZero: true }) ?? 'N/A')
				: formatExportValue(item, key, colDef, dataName);
		});
		if (currencyInsertAt === visibleColumns.length) exportRow[currencyLabel] = currencyOf(item) || 'N/A';
		return exportRow;
	});
}
